import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import AxiosInstance from "../../services/AxiosInstance";
import { REDUX_SLICES, YAAKE_BASE_URL } from "../../utils/constants";
import { getAPIEndPoints } from "../../utils/api";
import { InFlightNames, InFlightStatuses } from "../../types/types";
import { Actions } from "../../utils/actionType";
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "@firebase/auth";
import { firebaseAuth } from "../../config/firebase";
import {
  OnReceiveLoginWithPasswordType,
  OnReceiveReferralIncentiveAmountType,
  OnReceiveReferralsType,
  OnReceiveUpdateReferralsType,
  RequestLoginWithPasswordType,
  RequestPasswordResetType,
  RequestReferralIncentiveAmountType,
  RequestUpdateReferralsType,
} from "./adminPayloadTypes";
import dayjs from "dayjs";
import {
  Referral,
  Referrer,
  UserDetailsType,
} from "../../types/admin/adminTypes";

export interface AdminState {
  adminUId: string;
  adminEmail: string;
  referredUsers: Referrer[];
  referrals: Referral[];

  inFlights: {
    [InFlightNames.requestLoginWithPasswordInflight]: {
      status: InFlightStatuses;
    };
    [InFlightNames.requestLogoutInFlight]: {
      status: InFlightStatuses;
    };
    [InFlightNames.requestReferralsInFlight]: {
      status: InFlightStatuses;
    };
    [InFlightNames.requestReferredUsersInFlight]: {
      status: InFlightStatuses;
    };
    [InFlightNames.requestUpdateReferralsInFlight]: {
      status: InFlightStatuses;
    };
    [InFlightNames.requestPasswordResetInFlight]: {
      status: InFlightStatuses;
    };
    [InFlightNames.requestReferralIncentiveAmountInFlight]: {
      status: InFlightStatuses;
    };
  };
}

const initialState: AdminState = {
  adminUId: "",
  adminEmail: "",
  referrals: [],
  referredUsers: [],

  inFlights: {
    [InFlightNames.requestLoginWithPasswordInflight]: {
      status: InFlightStatuses.INITIAL,
    },
    [InFlightNames.requestLogoutInFlight]: {
      status: InFlightStatuses.INITIAL,
    },
    [InFlightNames.requestReferralsInFlight]: {
      status: InFlightStatuses.INITIAL,
    },
    [InFlightNames.requestReferredUsersInFlight]: {
      status: InFlightStatuses.INITIAL,
    },
    [InFlightNames.requestUpdateReferralsInFlight]: {
      status: InFlightStatuses.INITIAL,
    },
    [InFlightNames.requestPasswordResetInFlight]: {
      status: InFlightStatuses.INITIAL,
    },
    [InFlightNames.requestReferralIncentiveAmountInFlight]: {
      status: InFlightStatuses.INITIAL,
    },
  },
};

export const loginWithEmail = createAsyncThunk<
  OnReceiveLoginWithPasswordType,
  RequestLoginWithPasswordType
>(Actions.requestLoginWithEmail, async (data, { rejectWithValue }) => {
  try {
    const { email, password } = data;
    const userCredential = await signInWithEmailAndPassword(
      firebaseAuth,
      email,
      password
    );
    return {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
    };
  } catch (error: any) {
    return rejectWithValue({ uid: "", email: "" });
  }
});

export const requestAdminlogout = createAsyncThunk(
  Actions.requestAdminlogout,
  async (_, { rejectWithValue }) => {
    try {
      await signOut(firebaseAuth);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const requestPasswordReset = createAsyncThunk<
  void,
  RequestPasswordResetType
>(Actions.requestPasswordReset, async (data, { rejectWithValue }) => {
  const { email, adminNotificationApi } = data;
  try {
    await sendPasswordResetEmail(firebaseAuth, email);
    adminNotificationApi["success"]({
      message: "Admin",
      description: `Password Reset Email Sent Successfully`,
    });
  } catch (error: any) {
    adminNotificationApi["error"]({
      message: "Admin",
      description: `Failed to Send Password Reset Email`,
    });
    return rejectWithValue(error.message);
  }
});

export const initializeAuth = createAsyncThunk<UserDetailsType | null, void>(
  Actions.requestInitializeUser,
  async () => {
    return new Promise<UserDetailsType | null>((resolve) => {
      onAuthStateChanged(firebaseAuth, (user) => {
        if (user) {
          resolve({ uid: user.uid, email: user.email! });
        } else {
          resolve(null);
        }
      });
    });
  }
);

export const requestReferredUsers = createAsyncThunk(
  Actions.requestReferredUsers,
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        getAPIEndPoints.users() + `?limit=500`
      );

      return response.data.list;
    } catch (error: any) {
      return rejectWithValue([]);
    }
  }
);

export const requestReferrals = createAsyncThunk<OnReceiveReferralsType, void>(
  Actions.requestReferrals,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const referrals = await AxiosInstance.get(
        getAPIEndPoints.referrals() + "?limit=500"
      );

      const referredUsers = await dispatch(requestReferredUsers());

      return { referrals: referrals.data, users: referredUsers.payload };
    } catch (error: any) {
      return rejectWithValue({ referrals: [], users: [] });
    }
  }
);

export const requestReferralIncentiveAmount = createAsyncThunk<
  OnReceiveReferralIncentiveAmountType,
  RequestReferralIncentiveAmountType
>(
  Actions.requestReferralIncentiveDetails,
  async (data, { rejectWithValue }) => {
    const { user_fk } = data;
    try {
      const response = await AxiosInstance.get(
        getAPIEndPoints.yaakeNotifications.getReferralsIncentive(user_fk),
        { baseURL: YAAKE_BASE_URL }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue({ per_case: {}, commission: {} });
    }
  }
);

export const requestUpdateReferrals = createAsyncThunk<
  OnReceiveUpdateReferralsType,
  RequestUpdateReferralsType
>(Actions.requestUpdateReferrals, async (data, { rejectWithValue }) => {
  const { Id, adminNotificationApi, updateFor } = data;

  try {
    const dataToUpdate: any = {};

    if (data?.hasOwnProperty("clientFk")) {
      dataToUpdate.client_fk = data.clientFk;
    }

    if (data?.hasOwnProperty("modality")) {
      dataToUpdate.modality = data.modality?.join(",");
    }

    if (data?.hasOwnProperty("incentiveType")) {
      dataToUpdate.incentive_type = data.incentiveType;
    }

    if (data?.hasOwnProperty("referralOnboardedDate")) {
      dataToUpdate.onboarded_at = dayjs(data.referralOnboardedDate).format(
        "YYYY-MM-DD"
      );
    }

    if (data?.hasOwnProperty("incentiveEndDate")) {
      dataToUpdate.completed_at = dayjs(data.incentiveEndDate).format(
        "YYYY-MM-DD"
      );
    }

    if (data?.hasOwnProperty("referralAction")) {
      dataToUpdate.referral_action = data.referralAction;
    }

    if (data?.hasOwnProperty("incentiveTypePerCase")) {
      dataToUpdate.incentive_details = JSON.stringify(
        data.incentiveTypePerCase
      );
    }

    if (data?.hasOwnProperty("commission")) {
      dataToUpdate.incentive_details = JSON.stringify([
        { commission: data.commission },
      ]);
    }

    const referrals = await AxiosInstance.patch(
      getAPIEndPoints.updateReferrals(Id),
      {
        data: dataToUpdate,
      }
    );

    adminNotificationApi["success"]({
      message: "Lead Management - Update Referral",
      description: `Updated ${updateFor} for ${referrals.data.organisation_name.trim()}`,
    });

    return { referrals: referrals.data };
  } catch (error: any) {
    adminNotificationApi["error"]({
      message: "Lead Management - Update Referral",
      description: `Failed to Update ${updateFor}`,
    });
    return rejectWithValue({
      referrals: {},
    });
  }
});

const { reducer, actions } = createSlice({
  name: REDUX_SLICES.ADMIN,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginWithEmail.pending, (state) => {
        state.inFlights[InFlightNames.requestLoginWithPasswordInflight].status =
          InFlightStatuses.PENDING;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.adminUId = action.payload.uid;
        if (action.payload.email) {
          state.adminEmail = action.payload.email;
        }
        state.inFlights[InFlightNames.requestLoginWithPasswordInflight].status =
          InFlightStatuses.SUCCESS;
      })
      .addCase(loginWithEmail.rejected, (state) => {
        state.inFlights[InFlightNames.requestLoginWithPasswordInflight].status =
          InFlightStatuses.ERROR;
      })

      // ===================================================================================================================

      .addCase(
        initializeAuth.fulfilled,
        (state, action: PayloadAction<UserDetailsType | null>) => {
          if (action.payload?.uid && action.payload.email) {
            state.adminUId = action.payload.uid;
            state.adminEmail = action.payload?.email;
          } else {
            state.adminUId = "";
            state.adminEmail = "";
          }
        }
      )

      // ===================================================================================================================

      .addCase(requestAdminlogout.pending, (state) => {
        state.inFlights[InFlightNames.requestLogoutInFlight].status =
          InFlightStatuses.PENDING;
      })
      .addCase(requestAdminlogout.fulfilled, (state) => {
        state.inFlights[InFlightNames.requestLogoutInFlight].status =
          InFlightStatuses.SUCCESS;
      })
      .addCase(requestAdminlogout.rejected, (state) => {
        state.inFlights[InFlightNames.requestLogoutInFlight].status =
          InFlightStatuses.ERROR;
      })

      // ===================================================================================================================

      .addCase(requestPasswordReset.pending, (state) => {
        state.inFlights[InFlightNames.requestPasswordResetInFlight].status =
          InFlightStatuses.PENDING;
      })
      .addCase(requestPasswordReset.fulfilled, (state) => {
        state.inFlights[InFlightNames.requestPasswordResetInFlight].status =
          InFlightStatuses.SUCCESS;
      })
      .addCase(requestPasswordReset.rejected, (state) => {
        state.inFlights[InFlightNames.requestPasswordResetInFlight].status =
          InFlightStatuses.ERROR;
      })

      // ===================================================================================================================

      .addCase(requestReferrals.pending, (state) => {
        state.inFlights[InFlightNames.requestReferralsInFlight].status =
          InFlightStatuses.PENDING;
      })
      .addCase(requestReferrals.fulfilled, (state, action) => {
        const { referrals, users } = action.payload as any;

        const userMap = Object.fromEntries(
          users?.map((user: any) => [user.Id, user])
        );

        const mappedReferrals = referrals?.list?.map((referral: any) => ({
          ...referral,
          user: userMap[referral.user_fk] || null,
        }));

        state.referrals = mappedReferrals;
        state.inFlights[InFlightNames.requestReferralsInFlight].status =
          InFlightStatuses.SUCCESS;
      })
      .addCase(requestReferrals.rejected, (state) => {
        state.inFlights[InFlightNames.requestReferralsInFlight].status =
          InFlightStatuses.ERROR;
      })

      // ===================================================================================================================

      .addCase(requestReferredUsers.pending, (state) => {
        state.inFlights[InFlightNames.requestReferredUsersInFlight].status =
          InFlightStatuses.PENDING;
      })
      .addCase(requestReferredUsers.fulfilled, (state, action) => {
        state.referredUsers = action.payload as any;

        state.inFlights[InFlightNames.requestReferredUsersInFlight].status =
          InFlightStatuses.SUCCESS;
      })
      .addCase(requestReferredUsers.rejected, (state) => {
        state.inFlights[InFlightNames.requestReferredUsersInFlight].status =
          InFlightStatuses.ERROR;
      })

      // ===================================================================================================================

      .addCase(requestUpdateReferrals.pending, (state) => {
        state.inFlights[InFlightNames.requestUpdateReferralsInFlight].status =
          InFlightStatuses.PENDING;
      })
      .addCase(requestUpdateReferrals.fulfilled, (state, action) => {
        if (!action.payload) return;

        const index = state.referrals.findIndex(
          (referral) => referral.user_fk === action.payload.referrals.user_fk
        );

        if (index !== -1) {
          state.referrals[index] = {
            ...state.referrals[index],
            ...action.payload.referrals,
          };
        }

        state.inFlights[InFlightNames.requestUpdateReferralsInFlight].status =
          InFlightStatuses.SUCCESS;
      })
      .addCase(requestUpdateReferrals.rejected, (state) => {
        state.inFlights[InFlightNames.requestUpdateReferralsInFlight].status =
          InFlightStatuses.ERROR;
      })

      // ===================================================================================================================

      .addCase(requestReferralIncentiveAmount.pending, (state) => {
        state.inFlights[
          InFlightNames.requestReferralIncentiveAmountInFlight
        ].status = InFlightStatuses.PENDING;
      })
      .addCase(requestReferralIncentiveAmount.fulfilled, (state, action) => {
        if (!action.payload) return;

        state.referrals = state.referrals.map((referral) => {
          const incentiveData =
            action.payload.per_case[referral.Id] ||
            action.payload.commission[referral.Id] ||
            null;

          return {
            ...referral,
            incentiveData,
          };
        });

        state.inFlights[
          InFlightNames.requestReferralIncentiveAmountInFlight
        ].status = InFlightStatuses.SUCCESS;
      })
      .addCase(requestReferralIncentiveAmount.rejected, (state) => {
        state.inFlights[
          InFlightNames.requestReferralIncentiveAmountInFlight
        ].status = InFlightStatuses.ERROR;
      });
  },
});

export default reducer;
